/**
 * OutpostModal.jsx - Full-screen modal for managing an outpost
 *
 * Tabs:
 * - Buildings: 4 building slots
 * - Mercenaries: Hire/manage mercenaries
 * - Missions: Send squads on dungeon missions
 */

import React, { useState, useMemo } from 'react';
import { useGame } from '../../context/GameContext';
import { BIOMES } from '../../data/world-map/biomes';
import { buildings } from '../../data/buildings';
import { resources } from '../../data/resources';
import { DUNGEON_CONFIG } from '../../data/dungeon';
import {
  MERCENARY_CLASSES,
  getQualityRating,
  getHiringCost,
} from '../../data/outposts/mercenaries';
import {
  getSuccessRateDisplay,
  formatMissionTime,
} from '../../data/outposts/missions';
import { calculateMercenaryStats, calculateSquadPower } from '../../modules/mercenaries';
import './OutpostModal.css';

// Tab component
function TabButton({ id, label, icon, activeTab, onClick }) {
  return (
    <button
      className={`op-tab-btn ${activeTab === id ? 'active' : ''}`}
      onClick={() => onClick(id)}
    >
      <span className="op-tab-icon">{icon}</span>
      <span className="op-tab-label">{label}</span>
    </button>
  );
}

// Tier colors from DESIGN.md
const TIER_COLORS = {
  1: '#c4875c', 2: '#8090a0', 3: '#3ca03c', 4: '#5a9ad0', 5: '#a888d8',
  6: '#ffa030', 7: '#ffe040', 8: '#e83050', 9: '#20e0e0', 10: '#a050f0', 11: '#ffe040',
};

// Format production for display
const formatProduction = (production) => {
  if (!production) return null;
  return Object.entries(production).map(([res, amt]) => {
    const resInfo = resources[res] || { icon: '📦', name: res };
    return { res, amt, icon: resInfo.icon, name: resInfo.name };
  });
};

// Building slot component
function BuildingSlot({ slot, index, nodeId, biomeId }) {
  const {
    buildInOutpostSlot,
    playerResources,
    researchedTechs,
  } = useGame();

  const [showPicker, setShowPicker] = useState(false);

  // Get buildings available for this biome, filtered by research
  const biome = BIOMES[biomeId];
  const biomeBuildings = biome?.associatedBuildings || [];

  // Filter to only show buildings the player has researched
  const availableBuildings = biomeBuildings.filter(buildingId => {
    const building = buildings[buildingId];
    if (!building) return false;
    // If no unlock condition, it's always available
    if (!building.unlockCondition) return true;
    // If requires tech, check if researched
    if (building.unlockCondition.tech) {
      return researchedTechs?.includes(building.unlockCondition.tech);
    }
    return true;
  });

  const handleBuild = (buildingId) => {
    const result = buildInOutpostSlot(nodeId, index, buildingId);
    if (result.success) {
      setShowPicker(false);
    }
  };

  if (slot) {
    // Slot is occupied - show building info with production
    const building = buildings[slot];
    const production = formatProduction(building?.baseProduction);
    const tierColor = TIER_COLORS[building?.tier || 1];

    return (
      <div className="op-slot occupied" style={{ borderLeftColor: tierColor }}>
        <span className="op-slot-icon">{building?.icon || '🏠'}</span>
        <div className="op-slot-info">
          <span className="op-slot-name">{building?.name || 'Building'}</span>
          {production && (
            <div className="op-slot-production">
              {production.map(p => (
                <span key={p.res} className="op-prod-item" title={`+${p.amt}/s ${p.name}`}>
                  {p.icon}+{p.amt}/s
                </span>
              ))}
            </div>
          )}
          <span className="op-slot-capacity">+5 panel capacity</span>
        </div>
        <span className="op-slot-tier" style={{ color: tierColor }}>T{building?.tier || 1}</span>
      </div>
    );
  }

  if (showPicker) {
    return (
      <div className="op-slot picker-open">
        <div className="op-slot-picker">
          <div className="op-picker-header">
            <span>Select Building</span>
            <button className="op-picker-close" onClick={() => setShowPicker(false)}>×</button>
          </div>
          <div className="op-picker-list">
            {availableBuildings.map(buildingId => {
              const building = buildings[buildingId];
              if (!building) return null;

              const tierColor = TIER_COLORS[building.tier || 1];
              const production = formatProduction(building.baseProduction);

              // Calculate cost (tile cost is 2x base)
              const tileCost = Object.entries(building.baseCost || {}).map(([res, amt]) => ({
                res,
                need: amt * 2,
                have: playerResources[res] || 0,
                info: resources[res] || { icon: '📦', name: res },
              }));

              const canAfford = tileCost.every(c => c.have >= c.need);

              return (
                <button
                  key={buildingId}
                  className={`op-picker-item ${canAfford ? 'can-afford' : 'disabled'}`}
                  onClick={() => canAfford && handleBuild(buildingId)}
                  disabled={!canAfford}
                  style={{ borderLeftColor: tierColor }}
                >
                  <div className="op-picker-main">
                    <div className="op-picker-row">
                      <span className="op-picker-icon">{building.icon}</span>
                      <span className="op-picker-name">{building.name}</span>
                      <span className="op-picker-tier" style={{ color: tierColor }}>T{building.tier}</span>
                    </div>
                    {production && (
                      <div className="op-picker-production">
                        {production.map(p => (
                          <span key={p.res} className="op-picker-prod">
                            {p.icon}+{p.amt}/s
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="op-picker-costs">
                    {tileCost.map(c => (
                      <span
                        key={c.res}
                        className={`op-picker-cost ${c.have >= c.need ? 'has' : 'lacks'}`}
                        title={`${c.info.name}: ${Math.floor(c.have)}/${c.need}`}
                      >
                        {c.info.icon}<span className="cost-amt">{c.need}</span>
                        <span className="cost-have">/{Math.floor(c.have)}</span>
                      </span>
                    ))}
                  </div>
                </button>
              );
            })}
            {availableBuildings.length === 0 && (
              <div className="op-picker-empty">
                <span className="op-picker-empty-icon">🔬</span>
                <span>Research more techs to unlock buildings for this biome</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <button className="op-slot empty" onClick={() => setShowPicker(true)}>
      <span className="op-slot-plus">+</span>
      <span className="op-slot-text">Build</span>
    </button>
  );
}

// Mercenary card component
function MercenaryCard({ mercenary, onDismiss, onToggleSquad, isSelected }) {
  const stats = calculateMercenaryStats(mercenary);
  const quality = getQualityRating(mercenary.quality);
  const mercClass = MERCENARY_CLASSES[mercenary.class];

  const statusColors = {
    idle: '#80b858',
    mission: '#6080a8',
    dead: '#c06060',
  };

  return (
    <div className={`op-merc-card ${isSelected ? 'selected' : ''} ${mercenary.status}`}>
      <div className="op-merc-header">
        <span className="op-merc-portrait">{mercenary.portrait}</span>
        <div className="op-merc-info">
          <span className="op-merc-name">{mercenary.name}</span>
          <span className="op-merc-class">
            <span className="class-icon">{mercClass?.icon}</span>
            {mercClass?.name} Lv.{mercenary.level}
          </span>
        </div>
        <span
          className="op-merc-status"
          style={{ color: statusColors[mercenary.status] }}
        >
          {mercenary.status}
        </span>
      </div>

      <div className="op-merc-quality" style={{ color: quality.color }}>
        {'★'.repeat(quality.stars)}{'☆'.repeat(5 - quality.stars)} {quality.label}
      </div>

      <div className="op-merc-stats">
        <div className="op-stat">
          <span className="op-stat-label">HP</span>
          <span className="op-stat-value">{stats.health}</span>
        </div>
        <div className="op-stat">
          <span className="op-stat-label">ATK</span>
          <span className="op-stat-value">{stats.attack}</span>
        </div>
        <div className="op-stat">
          <span className="op-stat-label">DEF</span>
          <span className="op-stat-value">{stats.defense}</span>
        </div>
        <div className="op-stat">
          <span className="op-stat-label">CRIT</span>
          <span className="op-stat-value">{Math.round(stats.critChance * 100)}%</span>
        </div>
      </div>

      <div className="op-merc-actions">
        {mercenary.status === 'idle' && onToggleSquad && (
          <button
            className={`op-merc-btn ${isSelected ? 'remove' : 'add'}`}
            onClick={() => onToggleSquad(mercenary.id)}
          >
            {isSelected ? 'Remove' : 'Add to Squad'}
          </button>
        )}
        {mercenary.status === 'idle' && (
          <button
            className="op-merc-btn dismiss"
            onClick={() => onDismiss(mercenary.id)}
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}

// Hire mercenary panel
function HirePanel({ nodeId }) {
  const { hireMercenary, playerResources, OUTPOST_CONFIG, worldMap } = useGame();
  const [selectedClass, setSelectedClass] = useState(null);

  const outpost = worldMap.outposts[nodeId];
  const atCapacity = outpost?.mercenaryIds?.length >= OUTPOST_CONFIG.maxMercenariesPerOutpost;

  const handleHire = () => {
    if (!selectedClass || atCapacity) return;
    const result = hireMercenary(nodeId, selectedClass);
    if (result.success) {
      setSelectedClass(null);
    }
  };

  // Estimated cost (level 1-5)
  const estimatedCost = getHiringCost(3); // Average starting level

  return (
    <div className="op-hire-panel">
      <div className="op-hire-title">Hire Mercenary</div>

      {atCapacity ? (
        <div className="op-hire-full">Outpost at mercenary capacity ({OUTPOST_CONFIG.maxMercenariesPerOutpost})</div>
      ) : (
        <>
          <div className="op-hire-classes">
            {Object.values(MERCENARY_CLASSES).map(mercClass => (
              <button
                key={mercClass.id}
                className={`op-class-btn ${selectedClass === mercClass.id ? 'selected' : ''}`}
                onClick={() => setSelectedClass(mercClass.id)}
                style={{ borderColor: selectedClass === mercClass.id ? mercClass.color : undefined }}
              >
                <span className="op-class-icon">{mercClass.icon}</span>
                <span className="op-class-name">{mercClass.name}</span>
              </button>
            ))}
          </div>

          {selectedClass && (
            <div className="op-hire-details">
              <div className="op-hire-desc">
                {MERCENARY_CLASSES[selectedClass].description}
              </div>
              <div className="op-hire-cost">
                <span className="cost-label">Cost:</span>
                {Object.entries(estimatedCost).map(([res, amt]) => {
                  const resInfo = resources[res] || { icon: '💰', name: res };
                  const has = playerResources[res] || 0;
                  return (
                    <span key={res} className={`cost-item ${has >= amt ? '' : 'insufficient'}`}>
                      {resInfo.icon} {amt}
                    </span>
                  );
                })}
              </div>
              <button
                className="op-hire-btn"
                onClick={handleHire}
              >
                Hire {MERCENARY_CLASSES[selectedClass].name}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Mission selection panel
function MissionPanel({ nodeId }) {
  const {
    getSquadsAtOutpost,
    getMercenariesAtOutpost,
    createSquad,
    disbandSquad,
    startMercenaryMission,
    completeMercenaryMission,
    getMissionPreview,
    OUTPOST_CONFIG,
    worldMap,
  } = useGame();

  const [selectedMercs, setSelectedMercs] = useState([]);
  const [selectedTiers, setSelectedTiers] = useState({}); // Per-squad tier selection
  const [missionResult, setMissionResult] = useState(null);

  const squads = getSquadsAtOutpost(nodeId);
  const mercenaries = getMercenariesAtOutpost(nodeId);
  const idleMercs = mercenaries.filter(m => m.status === 'idle');

  // Get discovered dungeon/encounter tiles from worldMap
  const discoveredDungeons = useMemo(() => {
    if (!worldMap?.discoveredNodes) return [];

    const dungeons = [];
    Object.entries(worldMap.discoveredNodes).forEach(([tileId, node]) => {
      const biome = BIOMES[node.biome];
      if (biome?.isDungeonBiome) {
        // Find matching dungeon tier
        const dungeonTier = DUNGEON_CONFIG.TIERS?.find(t => t.id === biome.dungeonTier);
        if (dungeonTier) {
          dungeons.push({
            tileId,
            biome,
            tier: dungeonTier,
            name: `${biome.name}`,
          });
        }
      }
    });

    // Sort by tier
    dungeons.sort((a, b) => (a.tier.tier || 1) - (b.tier.tier || 1));
    return dungeons;
  }, [worldMap?.discoveredNodes]);

  // Fallback to all tiers if no dungeons discovered (for testing)
  const availableMissions = discoveredDungeons.length > 0
    ? discoveredDungeons
    : (DUNGEON_CONFIG.TIERS || []).map(tier => ({ tier, name: tier.name, biome: null }));

  // Toggle mercenary selection for new squad
  const toggleMercSelection = (mercId) => {
    setSelectedMercs(prev => {
      if (prev.includes(mercId)) {
        return prev.filter(id => id !== mercId);
      }
      if (prev.length >= OUTPOST_CONFIG.maxSquadSize) {
        return prev;
      }
      return [...prev, mercId];
    });
  };

  // Create squad from selected mercs
  const handleCreateSquad = () => {
    if (selectedMercs.length === 0) return;
    const result = createSquad(nodeId, selectedMercs);
    if (result.success) {
      setSelectedMercs([]);
    }
  };

  // Start a mission
  const handleStartMission = (squadId, tierId) => {
    const result = startMercenaryMission(squadId, tierId);
    if (result.success) {
      setSelectedTiers(prev => ({ ...prev, [squadId]: null }));
    }
  };

  // Complete a mission
  const handleCompleteMission = (squadId) => {
    const result = completeMercenaryMission(squadId);
    if (result.success) {
      setMissionResult(result.result);
    }
  };

  // Calculate power of selected mercs
  const selectedPower = useMemo(() => {
    const mercs = selectedMercs.map(id => mercenaries.find(m => m.id === id)).filter(Boolean);
    return calculateSquadPower(mercs);
  }, [selectedMercs, mercenaries]);

  // Count active missions
  const activeMissions = squads.filter(s => s.currentMission !== null).length;
  const completedMissions = squads.filter(s => s.currentMission && Date.now() >= s.currentMission.endTime).length;

  return (
    <div className="op-mission-panel">
      {/* Compact Header with Stats */}
      <div className="op-tab-header mission">
        <div className="op-header-stats">
          <span className="op-stat-chip">
            <span className="stat-icon">👥</span>
            <span className="stat-value">{squads.length}</span>
            <span className="stat-label">Squads</span>
          </span>
          <span className="op-stat-chip">
            <span className="stat-icon">🏃</span>
            <span className="stat-value">{activeMissions}</span>
            <span className="stat-label">Active</span>
          </span>
          {completedMissions > 0 && (
            <span className="op-stat-chip highlight">
              <span className="stat-icon">✨</span>
              <span className="stat-value">{completedMissions}</span>
              <span className="stat-label">Ready</span>
            </span>
          )}
          <span className="op-stat-chip">
            <span className="stat-icon">🗺️</span>
            <span className="stat-value">{discoveredDungeons.length}</span>
            <span className="stat-label">Dungeons</span>
          </span>
        </div>
        <div className="op-header-hint">
          <strong>70% loot</strong> of manual runs • <span className="warning">💀 30% death</span> on failure
        </div>
      </div>

      {/* How Missions Work - Compact Explanation */}
      <div className="op-mission-info">
        <div className="op-info-row">
          <span className="info-step"><strong>1.</strong> Form squads from idle mercenaries</span>
          <span className="info-step"><strong>2.</strong> Send to discovered dungeons</span>
          <span className="info-step"><strong>3.</strong> Collect rewards on return</span>
        </div>
      </div>

      {/* No Dungeons Warning - Styled as Section */}
      {discoveredDungeons.length === 0 && (
        <div className="op-mission-section op-warning-section">
          <div className="op-empty-state">
            <span className="empty-icon">🗺️</span>
            <span className="empty-text">No dungeons discovered yet</span>
            <span className="empty-hint">Explore the world map to find dungeons for missions</span>
          </div>
        </div>
      )}

      {/* Active Squads */}
      <div className="op-mission-section">
        <div className="op-section-title">Active Squads ({squads.length})</div>
        {squads.length === 0 ? (
          <div className="op-empty-state small">
            <span className="empty-icon">👥</span>
            <span className="empty-text">No squads formed</span>
            <span className="empty-hint">Select mercenaries below to create one</span>
          </div>
        ) : (
          <div className="op-squad-list">
            {squads.map(squad => {
              const hasActiveMission = squad.currentMission !== null;
              const missionComplete = hasActiveMission && Date.now() >= squad.currentMission.endTime;

              return (
                <div key={squad.id} className="op-squad-card">
                  <div className="op-squad-header">
                    <span className="op-squad-name">Squad ({squad.members.length} mercs)</span>
                    <span className="op-squad-power">Power: {Math.round(squad.power)}</span>
                  </div>

                  <div className="op-squad-members">
                    {squad.members.map(m => (
                      <span key={m.id} className="op-squad-member" title={m.name}>
                        {MERCENARY_CLASSES[m.class]?.icon}
                      </span>
                    ))}
                  </div>

                  {hasActiveMission ? (
                    <div className="op-squad-mission">
                      {missionComplete ? (
                        <button
                          className="op-collect-btn"
                          onClick={() => handleCompleteMission(squad.id)}
                        >
                          Collect Rewards
                        </button>
                      ) : (
                        <div className="op-mission-progress">
                          <span className="op-mission-target">
                            {DUNGEON_CONFIG.TIERS?.find(t => t.id === squad.currentMission.targetTier)?.name || 'Mission'}
                          </span>
                          <span className="op-mission-time">
                            {formatMissionTime(squad.currentMission.endTime - Date.now())}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="op-squad-actions">
                      <select
                        className="op-tier-select"
                        value={selectedTiers[squad.id] || ''}
                        onChange={(e) => setSelectedTiers(prev => ({ ...prev, [squad.id]: e.target.value }))}
                      >
                        <option value="">Select Dungeon...</option>
                        {availableMissions.map((mission, idx) => {
                          const preview = getMissionPreview(squad.id, mission.tier.id);
                          const successDisplay = preview ? getSuccessRateDisplay(preview.successRate) : null;
                          return (
                            <option key={mission.tileId || `tier-${idx}`} value={mission.tier.id}>
                              {mission.tier.icon} {mission.name} ({successDisplay?.percent || 0}%)
                            </option>
                          );
                        })}
                      </select>
                      {selectedTiers[squad.id] && (
                        <button
                          className="op-start-btn"
                          onClick={() => handleStartMission(squad.id, selectedTiers[squad.id])}
                        >
                          Start Mission
                        </button>
                      )}
                      <button
                        className="op-disband-btn"
                        onClick={() => disbandSquad(squad.id)}
                      >
                        Disband
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Discovered Dungeons */}
      {discoveredDungeons.length > 0 && (
        <div className="op-mission-section op-dungeons-section">
          <div className="op-section-title">Available Targets ({discoveredDungeons.length})</div>
          <div className="op-dungeon-list">
            {discoveredDungeons.map(dungeon => (
              <div key={dungeon.tileId} className="op-dungeon-chip">
                <span className="dc-icon">{dungeon.biome?.icon || dungeon.tier.icon}</span>
                <span className="dc-name">{dungeon.name}</span>
                <span className="dc-tier">T{dungeon.tier.tier}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form New Squad */}
      <div className="op-mission-section">
        <div className="op-section-title">
          Form New Squad
          {selectedMercs.length > 0 && (
            <span className="op-selected-count">
              ({selectedMercs.length}/{OUTPOST_CONFIG.maxSquadSize})
            </span>
          )}
        </div>

        {idleMercs.length === 0 ? (
          <div className="op-empty-state small">
            <span className="empty-icon">😴</span>
            <span className="empty-text">No idle mercenaries</span>
            <span className="empty-hint">Hire mercenaries or wait for missions to complete</span>
          </div>
        ) : (
          <>
            <div className="op-merc-select-grid">
              {idleMercs.map(merc => (
                <button
                  key={merc.id}
                  className={`op-merc-select-btn ${selectedMercs.includes(merc.id) ? 'selected' : ''}`}
                  onClick={() => toggleMercSelection(merc.id)}
                >
                  <span className="ms-portrait">{merc.portrait}</span>
                  <span className="ms-class">{MERCENARY_CLASSES[merc.class]?.icon}</span>
                  <span className="ms-level">Lv.{merc.level}</span>
                </button>
              ))}
            </div>

            {selectedMercs.length > 0 && (
              <div className="op-squad-preview">
                <span className="sp-power">Power: {Math.round(selectedPower)}</span>
                <button className="op-create-squad-btn" onClick={handleCreateSquad}>
                  Create Squad
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Mission Result Modal */}
      {missionResult && (
        <div className="op-result-overlay" onClick={() => setMissionResult(null)}>
          <div className="op-result-modal" onClick={e => e.stopPropagation()}>
            <div className={`op-result-header ${missionResult.success ? 'success' : 'failure'}`}>
              <h3>{missionResult.success ? 'Mission Success!' : 'Mission Failed'}</h3>
            </div>
            <div className="op-result-body">
              {missionResult.casualties.length > 0 && (
                <div className="op-casualties">
                  <span className="casualty-label">Casualties:</span>
                  <span className="casualty-count">{missionResult.casualties.length} mercenaries lost</span>
                </div>
              )}
              {missionResult.rewards.experience > 0 && (
                <div className="op-reward-xp">
                  +{missionResult.rewards.experience} Player XP
                </div>
              )}
              {missionResult.rewards.resources.gold > 0 && (
                <div className="op-reward-gold">
                  +{missionResult.rewards.resources.gold} Gold
                </div>
              )}
            </div>
            <button className="op-result-close" onClick={() => setMissionResult(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Trade Route Section component for outposts
function TradeRouteSection({ nodeId }) {
  const {
    worldMap,
    playerResources,
    buildTradeRouteSegment,
    getTradeRouteCost,
    isTileOnTradeRoute,
  } = useGame();

  const isOnTradeRoute = isTileOnTradeRoute(nodeId);
  const routeCost = getTradeRouteCost(nodeId);

  // Check if can build route (must be adjacent to existing route)
  const canBuildRoute = useMemo(() => {
    if (isOnTradeRoute) return false;
    const segments = worldMap.tradeRoutes?.segments || {};
    const discoveredNodes = worldMap.discoveredNodes || {};

    // Check if any adjacent tile is on the trade route
    const coords = nodeId.match(/node_(-?\d+)_(-?\d+)/);
    if (!coords) return false;
    const q = parseInt(coords[1]);
    const r = parseInt(coords[2]);

    const neighbors = [
      [q + 1, r], [q - 1, r], [q, r + 1], [q, r - 1], [q + 1, r - 1], [q - 1, r + 1]
    ];

    for (const [nq, nr] of neighbors) {
      const neighborId = `node_${nq}_${nr}`;
      if (neighborId === 'node_0_0' || neighborId === 'kingdom') return true;
      if (segments[neighborId]?.built && discoveredNodes[neighborId]) {
        // Check if neighbor is connected to kingdom
        const connectedTiles = worldMap.tradeRoutes?.connectedToKingdom;
        if (connectedTiles instanceof Set && connectedTiles.has(neighborId)) {
          return true;
        }
      }
    }
    return false;
  }, [nodeId, worldMap.tradeRoutes, worldMap.discoveredNodes, isOnTradeRoute]);

  // Check affordability
  const canAffordRoute = routeCost && Object.entries(routeCost).every(
    ([res, amt]) => (playerResources?.[res] || 0) >= amt
  );

  const handleBuildRoute = () => {
    buildTradeRouteSegment(nodeId);
  };

  return (
    <div className="op-trade-section">
      <div className="op-section-title">🛤️ Trade Route</div>
      {isOnTradeRoute ? (
        <div className="op-trade-connected">
          <span className="op-trade-status-icon">✅</span>
          <span className="op-trade-status-text">Connected to Kingdom</span>
        </div>
      ) : canBuildRoute ? (
        <div className="op-trade-build">
          <div className="op-trade-cost">
            {routeCost && Object.entries(routeCost).map(([res, amt]) => {
              const resourceInfo = resources[res] || { name: res, icon: '📦' };
              const have = playerResources?.[res] || 0;
              const hasEnough = have >= amt;
              return (
                <div key={res} className={`op-trade-cost-item ${hasEnough ? '' : 'insufficient'}`}>
                  <span className="op-trade-cost-icon">{resourceInfo.icon}</span>
                  <span className="op-trade-cost-amount">{amt}</span>
                  <span className={`op-trade-cost-have ${hasEnough ? 'has' : 'lacks'}`}>
                    ({have})
                  </span>
                </div>
              );
            })}
          </div>
          <button
            className={`op-trade-build-btn ${canAffordRoute ? '' : 'disabled'}`}
            onClick={handleBuildRoute}
            disabled={!canAffordRoute}
          >
            Build Trade Route
          </button>
        </div>
      ) : (
        <div className="op-trade-blocked">
          <span className="op-trade-status-icon">🔗</span>
          <span className="op-trade-status-text">Must be adjacent to existing route</span>
        </div>
      )}
    </div>
  );
}

// Main OutpostModal component
export function OutpostModal({ nodeId, onClose }) {
  const { worldMap, dismissMercenary, getMercenariesAtOutpost, getOutpostConstructionProgress, OUTPOST_CONFIG } = useGame();
  const [activeTab, setActiveTab] = useState('buildings');

  const outpost = worldMap.outposts[nodeId];
  const tileData = worldMap.discoveredNodes[nodeId];
  const biomeId = tileData?.biome || 'default';
  const biome = BIOMES[biomeId];

  const mercenaries = getMercenariesAtOutpost(nodeId);
  const constructionProgress = getOutpostConstructionProgress(nodeId);

  if (!outpost) {
    return null;
  }

  // If still constructing, show progress
  if (outpost.isConstructing) {
    return (
      <div className="op-overlay" onClick={onClose}>
        <div className="op-modal constructing" onClick={e => e.stopPropagation()}>
          <div className="op-header">
            <span className="op-biome-icon">{biome?.icon || '🏰'}</span>
            <h2>{outpost.name}</h2>
            <button className="op-close-btn" onClick={onClose}>×</button>
          </div>
          <div className="op-constructing">
            <div className="op-construct-icon">🏗️</div>
            <div className="op-construct-text">Under Construction</div>
            <div className="op-construct-progress">
              <div
                className="op-construct-bar"
                style={{ width: `${constructionProgress?.percent || 0}%` }}
              />
            </div>
            <div className="op-construct-time">
              {constructionProgress?.formattedRemaining || 'Calculating...'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="op-overlay" onClick={onClose}>
      <div className="op-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="op-header">
          <span className="op-biome-icon">{biome?.icon || '🏰'}</span>
          <h2>{outpost.name}</h2>
          <button className="op-close-btn" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="op-tabs">
          <TabButton id="buildings" label="Buildings" icon="🏗️" activeTab={activeTab} onClick={setActiveTab} />
          <TabButton id="mercenaries" label="Mercenaries" icon="⚔️" activeTab={activeTab} onClick={setActiveTab} />
          <TabButton id="missions" label="Missions" icon="📜" activeTab={activeTab} onClick={setActiveTab} />
        </div>

        {/* Tab Content */}
        <div className="op-content">
          {activeTab === 'buildings' && (
            <div className="op-buildings-tab">
              {/* Trade Route Section - at top */}
              <TradeRouteSection nodeId={nodeId} />

              {/* Compact Info + Stats Row */}
              <div className="op-tab-header buildings">
                <div className="op-header-stats">
                  <span className="op-stat-chip">
                    <span className="stat-icon">🏗️</span>
                    <span className="stat-value">{outpost.buildingSlots.filter(s => s).length}/4</span>
                    <span className="stat-label">Built</span>
                  </span>
                  <span className="op-stat-chip">
                    <span className="stat-icon">📦</span>
                    <span className="stat-value">+{outpost.buildingSlots.filter(s => s).length * 5}</span>
                    <span className="stat-label">Capacity</span>
                  </span>
                </div>
                <div className="op-header-hint">
                  Each building grants <strong>+5 panel capacity</strong> • Costs 2× base
                </div>
              </div>

              <div className="op-slots-grid">
                {outpost.buildingSlots.map((slot, index) => (
                  <BuildingSlot
                    key={index}
                    slot={slot}
                    index={index}
                    nodeId={nodeId}
                    biomeId={biomeId}
                  />
                ))}
              </div>
            </div>
          )}

          {activeTab === 'mercenaries' && (
            <div className="op-mercenaries-tab">
              {/* Compact Header with Stats */}
              <div className="op-tab-header merc">
                <div className="op-header-stats">
                  <span className="op-stat-chip">
                    <span className="stat-icon">⚔️</span>
                    <span className="stat-value">{mercenaries.length}/{OUTPOST_CONFIG?.maxMercenariesPerOutpost || 6}</span>
                    <span className="stat-label">Hired</span>
                  </span>
                  <span className="op-stat-chip">
                    <span className="stat-icon">😴</span>
                    <span className="stat-value">{mercenaries.filter(m => m.status === 'idle').length}</span>
                    <span className="stat-label">Idle</span>
                  </span>
                  <span className="op-stat-chip">
                    <span className="stat-icon">🏃</span>
                    <span className="stat-value">{mercenaries.filter(m => m.status === 'mission').length}</span>
                    <span className="stat-label">On Mission</span>
                  </span>
                </div>
                <div className="op-header-hint">
                  <strong>Random stats</strong> on hire • <span className="warning">⚠️ Permadeath</span> on failed missions
                </div>
              </div>

              <HirePanel nodeId={nodeId} />
              <div className="op-merc-roster">
                <div className="op-roster-title">Roster ({mercenaries.length})</div>
                {mercenaries.length === 0 ? (
                  <div className="op-empty-state small">
                    <span className="empty-icon">⚔️</span>
                    <span className="empty-text">No mercenaries hired</span>
                    <span className="empty-hint">Choose a class above to hire your first mercenary</span>
                  </div>
                ) : (
                  <div className="op-merc-list">
                    {mercenaries.map(merc => (
                      <MercenaryCard
                        key={merc.id}
                        mercenary={merc}
                        onDismiss={dismissMercenary}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'missions' && (
            <MissionPanel nodeId={nodeId} />
          )}
        </div>
      </div>
    </div>
  );
}

export default OutpostModal;
